import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateWhatsappLiveChatSettingsDocument,
    type CreateWhatsappLiveChatSettingsMutation,
    type CreateWhatsappLiveChatSettingsMutationVariables,
} from '../../../../api/mutations/createWhatsappLiveChatSettings';
import { type WhatsappLiveChatSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type AddWhatsappLiveChatSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = WhatsappLiveChatSettingsInput;

const initialValues: FormValues = {
    displayName: '',
    link: '',
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('link')
);

const AddWhatsappLiveChatSettingsForm = ({ moduleId, onComplete }: AddWhatsappLiveChatSettingsFormProps) => {
    const { t } = useTranslation(['whatsappLiveChatModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('whatsappLiveChatModuleDetails:settingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateWhatsappLiveChatSettingsMutation, CreateWhatsappLiveChatSettingsMutationVariables>({
                    mutation: CreateWhatsappLiveChatSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('whatsappLiveChatModuleDetails:settingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addWhatsappLiveChatSettings"
                    layout="vertical"
                    name="addWhatsappLiveChatSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('whatsappLiveChatModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('whatsappLiveChatModuleDetails:settingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('whatsappLiveChatModuleDetails:settingsDrawer.fields.link', {
                            returnObjects: true,
                        })}
                        name="link"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddWhatsappLiveChatSettingsForm;
